import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useProjects } from '../hooks/useProjects';
import { ArrowRight, ArrowUpDown, FolderKanban } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const Projects = () => {
  const { t, language } = useLanguage();
  const [order, setOrder] = useState('desc');
  const [filter, setFilter] = useState('all');
  const { projects } = useProjects();

  const sorted = useMemo(() => {
    const copy = [...projects];
    return order === 'asc'
      ? copy.reverse()
      : copy;
  }, [projects, order]);

  const contexts = useMemo(() => {
    const set = new Set();
    sorted.forEach((p) => set.add(p.context.en));
    return ['all', ...Array.from(set)];
  }, [sorted]);

  const filtered = useMemo(() => {
    if (filter === 'all') return sorted;
    return sorted.filter((p) => p.context.en === filter);
  }, [sorted, filter]);

  return (
    <div>
      {/* Header */}
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-3">
            <FolderKanban className="w-4 h-4" />
            <span className="w-8 h-px bg-[#4A8FD1]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight">
            {t('projects.title')}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
            {t('projects.subtitle')}
          </p>
        </div>
      </section>

      {/* Controls */}
      <section className="border-b border-white/10 bg-[#1A2231] sticky top-16 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {contexts.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-3 py-1.5 text-xs mono uppercase tracking-wider rounded border transition-colors ${
                  filter === c
                    ? 'bg-[#4A8FD1] text-slate-50 border-[#111827]'
                    : 'bg-[#1A2231] text-slate-400 border-white/10 hover:border-slate-400'
                }`}
              >
                {c === 'all' ? t('projects.filterAll') : c}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs mono uppercase tracking-wider text-slate-500">
              {t('projects.sortBy')}
            </span>
            <Select value={order} onValueChange={setOrder}>
              <SelectTrigger className="w-[170px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc" className="text-xs">
                  {t('projects.newest')}
                </SelectItem>
                <SelectItem value="asc" className="text-xs">
                  {t('projects.oldest')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, idx) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="project-card group block bg-[#1A2231] border border-white/10 rounded-lg overflow-hidden hover:border-[#4A8FD1] hover:shadow-lg transition-all duration-300"
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden bg-white/10 relative">
                  <img
                    src={p.thumbnail}
                    alt={p.title[language]}
                    className="project-thumb w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 px-2 py-1 bg-[#1A2231]/95 border border-white/10 rounded text-[10px] mono uppercase tracking-wider text-slate-300">
                    {p.year}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] mono uppercase tracking-widest text-[#4A8FD1] mb-2">
                    {p.context[language]}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-50 leading-snug group-hover:text-[#4A8FD1] transition-colors">
                    {p.title[language]}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-2 leading-relaxed">
                    {p.summary[language]}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.tools.slice(0, 3).map((tool) => (
                      <Badge
                        key={tool}
                        variant="secondary"
                        className="text-[10px] mono uppercase tracking-wider bg-white/10 text-slate-300 hover:bg-white/10 font-medium"
                      >
                        {tool}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-xs mono uppercase tracking-widest text-slate-50 group-hover:text-[#4A8FD1] group-hover:gap-2 transition-all">
                    {t('projects.readCase')}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
